import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { Injectable } from '@angular/core';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { DocumentService } from '../document.service';
import { TemplateLayoutService } from '@fi-app/template/template-layout.service';
import { DocumentModel } from '../document.model';
import { TemplateLayoutLanguageService } from '@fi-app/template/template-layout-language.service';
import { OperationOverlayLargeNg4Service } from '@fi-app/utilities/operation-overlay-large/operation-overlay-large.service';
import { FiDocumentViewDialogComponent } from '../document-view-dialog/document-view-dialog.component';
import { IDocumentViewData } from '../document-view/document-view.component';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { DocumentDialogAbsentContentLanguageComponent } from '../dialog-absent-content-language/dialog-absent-content-language.component';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';

interface IDefaultLanguageForViewData {
    defaultLanguageAbsent: boolean;
    languageId: number;
}

@Injectable()
export class DocumentViewService {
    constructor(
        private loadingService: LoadingService,
        private documentService: DocumentService,
        private templateLayoutService: TemplateLayoutService,
        private templateLayoutLanguageService: TemplateLayoutLanguageService,
        private operationOverlayService: OperationOverlayLargeNg4Service,
        private stateAccessCheckService: StateAccessCheckService,
        private fiDialogsService: FiDialogsService,
        private customerDataService: FiCustomerDataService,
        private appTextService: AppTextService,
    ) {}

    openDocumentViewDialog(documentId, params) {
        this.loadingService.trackPromise(this.documentService.GetDocument(documentId)).then((document) => {
            this.prepareEverythingForView(document)
                .then((documentViewData) => {
                    this.operationOverlayService.openOperationOverlayLargeDialog(
                        FiDocumentViewDialogComponent,
                        {
                            documentViewData: documentViewData,
                            document: document,
                        },
                        params,
                    );
                })
                .catch((reason) => {
                    if (reason !== 'cancel') throw reason;
                });
        });
    }

    prepareEverythingForViewFromExtendedSearch(documentId: number) {
        const promise = new Promise<{ document: DocumentModel; documentViewData: IDocumentViewData }>(
            (resolve, reject) => {
                this.loadingService.trackPromise(this.documentService.GetDocument(documentId)).then((document) => {
                    this.prepareEverythingForView(document).then((documentViewData) => {
                        resolve({
                            document: document,
                            documentViewData: documentViewData,
                        });
                    }, reject);
                }, reject);
            },
        );

        return promise;
    }

    prepareEverythingForView(document: DocumentModel, stateLanguageId?) {
        const templateLayout = this.templateLayoutService.GetLayout(document.Template, document);
        this.templateLayoutService.ApplyDocumentControlContentsToTemplate(
            document.DocumentControlContents,
            document.Template,
        );
        this.templateLayoutService.TemplatePlaceHolderCollectionToLayout(
            document.Template.PlaceHolderCollection,
            templateLayout,
        );

        const documentTemplateLayout = this.templateLayoutService.GetLayout(document.Template, document);
        this.templateLayoutService.TemplatePlaceHolderCollectionToLayout(
            document.Template.PlaceHolderCollection,
            documentTemplateLayout,
        );

        const hasEditAccess =
            this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.Document.Edit) &&
            document.AccessRightFilterCollection.HasEditAccess;

        return new Promise<IDocumentViewData>((resolve, reject) => {
            this.initDefaultLanguageForView(document, documentTemplateLayout, hasEditAccess, stateLanguageId)
                .then((langResult) => {
                    resolve({
                        templateLayout: templateLayout,
                        languageId: langResult.languageId,
                        documentTemplateLayout: documentTemplateLayout,
                        hasEditAccess: hasEditAccess,
                        defaultLanguageAbsent: langResult.defaultLanguageAbsent,
                    });
                })
                .catch((reason) => {
                    reject(reason);
                });
        });
    }

    initDefaultLanguageForView(
        document,
        documentTemplateLayout,
        WriteAccessRight,
        stateLanguageId,
    ): Promise<IDefaultLanguageForViewData> {
        /*if (WriteAccessRight === false && this.templateLayoutLanguageService.InitDefaultLanguageFromLayout(documentTemplateLayout)) {
            return Promise.resolve({
                defaultLanguageAbsent: false,
                languageId: this.templateLayoutLanguageService.selectedLanguage,
            });
        } else*/

        const langIds = this.templateLayoutLanguageService.GetLayoutLanguages(documentTemplateLayout);
        const isDocumentTranslatedToStateLang = langIds.some((landId) => landId === +stateLanguageId);

        if (stateLanguageId && isDocumentTranslatedToStateLang)
            return Promise.resolve({
                defaultLanguageAbsent: false,
                languageId: parseInt(stateLanguageId),
            });

        // TemplateLayoutLanguageService.InitDefaultLanguageFromDocument can already set TemplateLayoutLanguageService.selectedLanguage
        // so we need to remove it temporary to make InitDefaultLanguage work properly
        const tmp = this.templateLayoutLanguageService.selectedLanguage;
        this.templateLayoutLanguageService.selectedLanguage = null;
        if (this.templateLayoutLanguageService.InitDefaultLanguageFromLayout(documentTemplateLayout))
            return Promise.resolve({
                defaultLanguageAbsent: false,
                languageId: this.templateLayoutLanguageService.selectedLanguage,
            });

        this.templateLayoutLanguageService.selectedLanguage = tmp;

        if (langIds.length === 1 || this.customerDataService.customerData.UseDefaultContentLanguage) {
            return Promise.resolve({
                defaultLanguageAbsent: true,
                languageId: langIds[0],
            });
        }

        return this.showSelectLanguageDialog(documentTemplateLayout, langIds);
    }

    showSelectLanguageDialog(documentTemplateLayout, langIds): Promise<IDefaultLanguageForViewData> {
        return new Promise((resolve, reject) => {
            this.fiDialogsService
                .custom({
                    headerText: this.appTextService.get('common.select_language'),
                    yesButtonText: this.appTextService.get('done'),
                    contentComponentRef: DocumentDialogAbsentContentLanguageComponent,
                    contentComponentData: {
                        langIds: langIds,
                        messageText: this.appTextService.get('document.dialog_absent_content_language_text'),
                    },
                    config: {
                        panelClass: 'dialog-content-padding',
                    },
                })
                .onYesButtonClick((result) =>
                    resolve({
                        defaultLanguageAbsent: true,
                        languageId: result,
                    }),
                )
                .onNoButtonClick(() => reject('cancel'));
        });
    }
}
